import { apiFetch } from './api';

const apiUrl = import.meta.env.VITE_API_URL;

/**
 * A Visão geral de Sistema › Relatórios (só GB Code): a plataforma em números.
 *
 * "Usuário ativo" é quem ENTROU no período (decisão de 05/10/2026); cadastrados é o
 * denominador. Logins vêm do evento de login quando ele cobre o período; antes disso,
 * do refresh token, e a resposta avisa com `loginsAproximados`.
 *
 * docs/requisicoes-http/visao-geral.md (API)
 */

export interface VisaoGeralBase {
    empresas: number;
    empresasNovasNoPeriodo: number;
    empresasEmTeste: number;
    empresasComAssinaturaVigente: number;
    /** Nem em teste nem vigente: o teste acabou e não contratou. */
    empresasSemAssinatura: number;
    assinaturasPagas: number;
    assinaturasPendentes: number;
    assinaturasEmTeste: number;
    assinaturasCanceladas: number;
    assinaturasExpiradas: number;
    /** Foto de hoje, não série: a base não guarda histórico de plano. */
    mrrHoje: number;
    usuarios: number;
    usuariosNovosNoPeriodo: number;
    usuariosComGoogle: number;
}

export interface VisaoGeralUso {
    logins: number;
    /** Nulo quando o período foi contado pelo refresh token, que não sabe o método. */
    loginsGoogle: number | null;
    usuariosQueEntraram: number;
    empresasQueEntraram: number;
    loginsAproximados: boolean;
    medindoLoginsDesde: string | null;
    conexoesAtivas: number;
    conexoesTotal: number;
    conversasNoPeriodo: number;
    perguntasAssistenteNoPeriodo: number;
    agentesComBotLigado: number;
    empresasComBotLigado: number;
}

export interface PontoDia { dia: string; usuarios: number; logins: number }
export interface PontoMes { mes: string; valor: number }

export interface VisaoGeralSeries {
    entradasPorDia: PontoDia[];
    empresasNovasPorMes: PontoMes[];
    conversasPorMes: PontoMes[];
}

export type SituacaoEmpresa = 'vigente' | 'teste' | 'sem_assinatura';

export interface VisaoGeralEmpresa {
    empresaId: number;
    nome: string;
    situacao: SituacaoEmpresa;
    usuarios: number;
    entraramNoPeriodo: number;
    ultimaSessao: string | null;
    /** Última vez que alguém usou o CRM com a tela aberta (precisão de 5 min). Nunca é anterior ao login. */
    ultimoAcesso: string | null;
}

/** Um usuário da empresa aberta na lista. Sessões do modo suporte não contam. */
export interface VisaoGeralUsuario {
    usuarioId: number;
    nome: string;
    email: string;
    perfil: string | null;
    entradasNoPeriodo: number;
    ultimaSessao: string | null;
    /** Última vez que usou o CRM com a tela aberta (precisão de 5 min). Nunca é anterior ao login. */
    ultimoAcesso: string | null;
}

export interface VisaoGeralResposta {
    de: string;
    ate: string;
    base: VisaoGeralBase;
    uso: VisaoGeralUso;
    series: VisaoGeralSeries;
    empresas: VisaoGeralEmpresa[];
}

interface Envelope<T> { sucesso: boolean; mensagem: string; dados: T }

const ler = async <T>(resposta: Response): Promise<T> => {
    const corpo = await resposta.json().catch(() => null) as Envelope<T> | null;
    if (!resposta.ok || !corpo?.sucesso) {
        throw new Error(corpo?.mensagem || `Falha ao carregar (${resposta.status}).`);
    }
    return corpo.dados;
};

export const VisaoGeralSistemaService = {
    async buscar(de: string, ate: string): Promise<VisaoGeralResposta> {
        const resposta = await apiFetch(`${apiUrl}/api/sistema/visao-geral?de=${de}&ate=${ate}`);
        return ler<VisaoGeralResposta>(resposta);
    },

    async usuariosDaEmpresa(empresaId: number, de: string, ate: string): Promise<VisaoGeralUsuario[]> {
        const resposta = await apiFetch(`${apiUrl}/api/sistema/visao-geral/empresas/${empresaId}/usuarios?de=${de}&ate=${ate}`);
        return ler<VisaoGeralUsuario[]>(resposta);
    },
};
